import {
  Legend,
  ScoreCardHeader,
  ScoreTables,
} from "../../../components/atoms/tables";
import { vw } from "../../../lib/utils/responsiveUtils";
import {
  courseLabelOf,
  courseOrderOf,
  holeInCourseOf,
  needsUserPar,
  toScoreSections,
} from "../../../lib/utils/score.utils";
import type { ScoreCard } from "../../../@apis/dto/response/score.response";
import type { OvertimeScoreCardResponse } from "../../../@apis/dto/response/torunament.response";
import Button from "../../../components/atoms/button";
import OvertimeAccordion from "../../../components/atoms/OvertimeAccordion";

interface ScorecardTabProps {
  myScoreCardItem?: ScoreCard;
  scoreCardList: ScoreCard[];
  currentOvertimes: OvertimeScoreCardResponse["rounds"][number][];
  currentUserId?: number | null;
  pendingPars: Record<number, number>;
  pendingScores: Record<number, number>;
  tournamentId?: number | null;
  currentHoleNumber: number;
  onOpenDrawer: (item: ScoreCard, holeNo: number) => void;
  onOpenParDrawer: (item: ScoreCard, holeNo: number) => void;
}

export default function ScorecardTab({
  myScoreCardItem,
  scoreCardList,
  currentOvertimes,
  currentUserId,
  pendingPars,
  pendingScores,
  tournamentId,
  currentHoleNumber,
  onOpenDrawer,
  onOpenParDrawer,
}: ScorecardTabProps) {
  return (
    <>
      {/* 내 요약 영역 */}
      <div className="flex items-center justify-between bg-[#222] p-[20px] rounded-[10px] mt-[15px] gap-x-[4px]">
        <div className="flex items-center gap-[10px] flex-1 min-w-0">
          <div
            className="flex items-center justify-center rounded-full bg-[#333] shrink-0"
            style={{ width: vw(48), height: vw(48) }}
          >
            <img
              style={{ width: vw(24), height: vw(24) }}
              src="/icons/ic-face.webp"
              alt="face"
            />
          </div>
          <span className="text-[#fff] font-bold text-xl truncate min-w-0">
            {myScoreCardItem?.userName}(나)
          </span>
        </div>

        <div className="flex flex-col items-end leading-none shrink-0">
          <span className="text-white font-bold text-2xl leading-none">
            {myScoreCardItem?.totalScore}
          </span>
          {myScoreCardItem?.isComplete && (
            <span className=" text-[#fff] font-[500]">
              (
              {myScoreCardItem?.overUnderScore == null
                ? ""
                : myScoreCardItem.overUnderScore > 0
                  ? `+${myScoreCardItem.overUnderScore}`
                  : myScoreCardItem.overUnderScore}
              )
            </span>
          )}
        </div>
      </div>

      {/* 연장전 아코디언 */}
      {currentOvertimes.length > 0 && (
        <div className="space-y-5">
          {currentOvertimes
            .flatMap((round) => round.overtimes)
            .sort((a, b) => {
              // 진행 중인 연장전은 최상단에 노출
              if (a.status === "IN_PROGRESS" && b.status !== "IN_PROGRESS")
                return -1;
              if (a.status !== "IN_PROGRESS" && b.status === "IN_PROGRESS")
                return 1;
              // 종료된 연장전은 차수(1차, 2차…) 순서대로 정렬
              return a.sequence - b.sequence;
            })
            .map((overtime) => (
              <OvertimeAccordion
                key={overtime.overtimeId}
                overtime={overtime}
                currentUserId={currentUserId ?? 0}
                defaultOpen={overtime.status === "CONFIRMED"}
              />
            ))}
        </div>
      )}

      {/* 스코어 표 & 입력 버튼 */}
      {scoreCardList.map((item, index) => {
        // 내 카드(index 0)에는 아직 미저장된 par/score를 표에 반영
        const sections = toScoreSections(
          item,
          index === 0 ? { pars: pendingPars, scores: pendingScores } : undefined,
        );

        return (
          <div className="mt-[15px] mb-[15px]" key={item.scoreCardId}>
            {/* 첫 번째(내 카드) 외에는 상단 이름/점수 라인 */}
            {index !== 0 && (
              <ScoreCardHeader card={item} className="mt-[35px]" />
            )}

            {!tournamentId && (
              <Button
                type="button"
                variant="white"
                onClick={() => onOpenDrawer(item, currentHoleNumber)}
                className="mt-[15px] h-auto min-h-[50px] w-full whitespace-normal rounded-[25px] bg-white items-center justify-center gap-[10px] px-[16px] py-[12px]"
              >
                <img className="size-4 shrink-0" src="/icons/ic-score.webp" />
                <span className="min-w-0 text-[#222] font-[500] text-lg leading-snug break-keep text-center">
                  {courseLabelOf(item, courseOrderOf(currentHoleNumber))}{" "}
                  {holeInCourseOf(currentHoleNumber)}홀{" "}
                  {item.isComplete ? "스코어 수정" : "스코어 입력"}
                </span>
              </Button>
            )}

            {/* 테이블 */}
            <ScoreTables
              sections={sections}
              currentHoleNo={!tournamentId ? currentHoleNumber : undefined}
              onClickCell={(holeNo, rowType) => {
                if (tournamentId || index !== 0) return;
                if (rowType === "par") {
                  if (!needsUserPar(item, holeNo)) return;
                  onOpenParDrawer(item, holeNo);
                } else {
                  onOpenDrawer(item, holeNo);
                }
              }}
            />
            <Legend />
          </div>
        );
      })}
    </>
  );
}
